import { useState, useEffect, useCallback } from 'react';
import { useOrgStore } from '@/stores/orgStore';
import type { ColumnConfig } from '@/components/ui/ColumnChooser';

export interface UseColumnVisibilityOptions {
  storageKey: string;
  defaultColumns: ColumnConfig[];
}

export function useColumnVisibility({
  storageKey,
  defaultColumns
}: UseColumnVisibilityOptions) {
  const activeOrgId = useOrgStore((state) => state.activeOrgId);
  const orgScopedKey = `${storageKey}:${activeOrgId || 'default'}`;

  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Try to load from localStorage
    try {
      const stored = localStorage.getItem(orgScopedKey);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        return defaultColumns.map(col => ({
          ...col,
          visible: parsed[col.key] !== undefined ? parsed[col.key] : col.visible
        }));
      }
    } catch (error) {
      console.error('Failed to load column visibility from localStorage:', error);
    }
    return defaultColumns;
  });

  // Persist to localStorage whenever columns change
  useEffect(() => {
    try {
      const visibilityMap = columns.reduce((acc, col) => {
        acc[col.key] = col.visible;
        return acc;
      }, {} as Record<string, boolean>);
      localStorage.setItem(orgScopedKey, JSON.stringify(visibilityMap));
    } catch (error) {
      console.error('Failed to save column visibility to localStorage:', error);
    }
  }, [columns, orgScopedKey]);

  const updateColumns = useCallback((newColumns: ColumnConfig[]) => {
    setColumns(newColumns);
  }, []);

  const resetColumns = useCallback(() => {
    setColumns(defaultColumns);
  }, [defaultColumns]);

  const visibleColumns = columns.filter(col => col.visible);

  return {
    columns,
    visibleColumns,
    updateColumns,
    resetColumns
  };
}
